// Одна беседа на весь экран (M6.2, M6.3).
//
// Раскладок на телефоне нет: экран показывает ровно одну беседу и занимает
// всю область под шапкой. Поток — тот же, что на десктопе: SessionStream из
// transport/ws и срез в logStore. Своего транспорта, своего протокола и своей
// истории телефон не заводит, иначе строки расходились бы с окном на большом
// экране.
//
// Отличается только рисовка. Десктопная колонка «глиф + текст» на 390 px
// съедает треть ширины, поэтому лог собирается в пузыри: свои справа, агента
// слева, служебное — узкой строкой между ними.

import {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react'
import { api } from '../api'
import type { PermissionRequest, TaskInfo } from '../protocol'
import { describeError, useAppStore } from '../store/appStore'
import { useLogStore, usePane } from '../store/logStore'
import type { LogLine } from '../store/logStore'
import QuestionCard from '../panes/QuestionCard'
import { inline } from '../panes/inline'
import AgentsBlock from '../panes/AgentsBlock'
import VoiceLine from '../panes/VoiceLine'
import VoicePending from '../panes/VoicePending'
import { SessionStream, deliverInput } from '../transport/ws'
import type { PermissionOptions } from '../transport/ws'
import { queueAction, registerReplySender } from './offline'
import DictationSheet from './DictationSheet'
import './chat.css'

/** Сколько последних строк рисуем. Хвост лога с сервера — 2000 строк, и все
 *  они на телефоне не нужны: виртуализации здесь нет, а память и время кадра
 *  есть. Всё, что выше, остаётся в сторе и доступно на десктопе. */
const TAIL = 400
/** Длина служебной строки (вызов инструмента, его вывод) в пузырьковом логе. */
const CUT = 160

function cut(text: string): string {
  return text.length > CUT ? `${text.slice(0, CUT - 1)}…` : text
}

/** Вызов инструмента и его вывод — одной строкой: разворачивать простыню
 *  вывода на телефоне некуда, а знать, чем агент занят, нужно. */
function toolText(line: LogLine): string {
  const head = line.tool ?? (line.kind === 'result' ? 'вывод' : 'инструмент')
  const body = (line.text ?? line.rows?.[0] ?? '').replace(/\s+/g, ' ').trim()
  return cut(body ? `${head}: ${body}` : head)
}

/** Что именно просит разрешить агент — короткой строкой, без JSON целиком. */
function permSummary(input: Record<string, unknown>): string {
  const guess = input.command ?? input.file_path ?? input.path ?? input.pattern
  if (typeof guess === 'string') return cut(guess)
  try {
    return cut(JSON.stringify(input))
  } catch {
    return ''
  }
}

type BlockKind = 'me' | 'agent' | 'sys' | 'warn' | 'tool' | 'voice'

interface Block {
  kind: BlockKind
  seq: number
  text: string
  /** строка ещё дописывается стримом — рисуем каретку */
  partial: boolean
  /** голосовое: id сохранённой расшифровки */
  voiceId?: string
}

/** Строки лога → пузыри.
 *
 *  Подряд идущие строки агента склеиваются в один пузырь: сервер шлёт абзац
 *  построчно (продолжение помечено пустым gutter), и без склейки один ответ
 *  рассыпался бы на десяток пузырей. */
function toBlocks(lines: LogLine[]): Block[] {
  const out: Block[] = []
  for (const line of lines) {
    const text = (line.text ?? '').replace(/\s+$/, '')
    switch (line.kind) {
      case 'me':
        out.push({ kind: 'me', seq: line.seq, text, partial: false })
        break
      case 'agent': {
        const prev = out[out.length - 1]
        if (prev && prev.kind === 'agent') {
          prev.text = prev.text ? `${prev.text}\n${text}` : text
          prev.partial = !!line.partial
        } else {
          out.push({ kind: 'agent', seq: line.seq, text, partial: !!line.partial })
        }
        break
      }
      case 'tool':
      case 'result':
        // вызов AskUserQuestion — JSON вопросов, их показывает карточка ниже
        if (line.kind === 'tool' && line.tool === 'AskUserQuestion') break
        out.push({ kind: 'tool', seq: line.seq, text: toolText(line), partial: false })
        break
      case 'warn':
        out.push({ kind: 'warn', seq: line.seq, text, partial: false })
        break
      case 'perm':
        if (line.perm?.toolName === 'AskUserQuestion') break
        out.push({
          kind: 'sys',
          seq: line.seq,
          text: line.perm ? `запрос разрешения: ${line.perm.toolName}` : text,
          partial: false,
        })
        break
      case 'voice':
        if (line.voiceId) {
          out.push({ kind: 'voice', seq: line.seq, text, partial: false, voiceId: line.voiceId })
          break
        }
        out.push({ kind: 'sys', seq: line.seq, text, partial: false })
        break
      default:
        out.push({ kind: 'sys', seq: line.seq, text, partial: false })
        break
    }
  }
  return out.filter((b) => b.text.trim().length > 0 || b.partial)
}

/** пустой список один на всех — иначе новый [] на каждый рендер */
const NO_TASKS: TaskInfo[] = []

interface ThreadProps {
  sessionId: string
  onRespond(id: string, behavior: 'allow' | 'deny', opts?: PermissionOptions): void
}

/**
 * Лента реплик одной сессии. Отдельным компонентом, потому что ровно её же
 * показывает шторка агента (M5.2): диалог там такой же, меняется только
 * рамка вокруг.
 */
export function ChatThread({ sessionId, onRespond }: ThreadProps) {
  const lines = usePane(sessionId, (s) => s.lines)
  const pending = usePane(sessionId, (s) => s.pending)
  const conn = usePane(sessionId, (s) => s.conn)
  const state = usePane(sessionId, (s) => s.session?.state ?? 'idle')
  const tasks = usePane(sessionId, (s) => s.session?.tasks) ?? NO_TASKS

  const scRef = useRef<HTMLDivElement | null>(null)
  // Прилипание к низу держим в ref: перерисовывать ленту на каждый пиксель
  // прокрутки нельзя, это самый горячий экран телефона.
  const stickRef = useRef(true)
  const [atBottom, setAtBottom] = useState(true)

  const blocks = useMemo(
    () => toBlocks(lines.length > TAIL ? lines.slice(-TAIL) : lines),
    [lines],
  )

  useLayoutEffect(() => {
    const el = scRef.current
    if (el && stickRef.current) el.scrollTop = el.scrollHeight
  }, [blocks, pending])

  const onScroll = useCallback(() => {
    const el = scRef.current
    if (!el) return
    const stick = el.scrollHeight - el.scrollTop - el.clientHeight <= 24
    if (stick !== stickRef.current) {
      stickRef.current = stick
      setAtBottom(stick)
    }
  }, [])

  const jump = useCallback(() => {
    const el = scRef.current
    if (!el) return
    stickRef.current = true
    setAtBottom(true)
    el.scrollTop = el.scrollHeight
  }, [])

  const perm: PermissionRequest | undefined = pending[0]

  return (
    <div className="ab-thread">
      <div className="ab-thread__scroll" ref={scRef} onScroll={onScroll}>
        {conn === 'connecting' || conn === 'reconnecting' ? (
          <div className="ab-thread__sys">
            {conn === 'connecting' ? 'подключаюсь…' : 'переподключаюсь…'}
          </div>
        ) : null}

        {blocks.length === 0 ? (
          <div className="ab-thread__empty">
            Беседа пустая. Напишите или надиктуйте первую реплику.
          </div>
        ) : null}

        {blocks.map((b) => (
          b.kind === 'voice' && b.voiceId ? (
            <div key={b.seq} className="ab-thread__voice">
              <VoiceLine sessionId={sessionId} voiceId={b.voiceId} text={b.text} touch />
            </div>
          ) : b.kind === 'me' || b.kind === 'agent' ? (
            <div key={b.seq} className={`ab-bubble ab-bubble--${b.kind}`}>
              {b.kind === 'agent' ? inline(b.text, `b${b.seq}`) : b.text}
              {b.partial ? <i className="ab-bubble__caret" /> : null}
            </div>
          ) : (
            <div key={b.seq} className={`ab-thread__${b.kind}`}>{b.text}</div>
          )
        ))}

        <AgentsBlock tasks={tasks} />

        {state === 'running' && !perm ? (
          <div className="ab-thread__think">думаю…</div>
        ) : null}

        {/* Разрешение спрашивают редко, но если его не у кого спросить — агент
            стоит намертво. Карточка живёт в конце ленты, чтобы автоскролл сам
            подвёз её к пальцу. */}
        {perm?.toolName === 'AskUserQuestion' ? (
          <QuestionCard key={perm.id} sessionId={sessionId} perm={perm} onRespond={onRespond} touch />
        ) : perm ? (
          <div className="ab-mperm">
            <div className="ab-mperm__head">агенту нужно разрешение</div>
            <div className="ab-mperm__tool">{perm.toolName}</div>
            <div className="ab-mperm__what">{permSummary(perm.input)}</div>
            <div className="ab-mperm__row">
              <button
                type="button"
                className="ab-mperm__btn"
                onClick={() => onRespond(perm.id, 'deny')}
              >
                запретить
              </button>
              <button
                type="button"
                className="ab-mperm__btn ab-mperm__btn--allow"
                onClick={() => onRespond(perm.id, 'allow')}
              >
                разрешить
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {atBottom ? null : (
        <button type="button" className="ab-thread__down" onClick={jump} aria-label="вниз">
          ↓
        </button>
      )}
    </div>
  )
}

interface Props { sessionId: string }

export default function ChatScreen({ sessionId }: Props) {
  const streamRef = useRef<SessionStream | null>(null)
  const [dictating, setDictating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [savedPath, setSavedPath] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [reviving, setReviving] = useState(false)

  const draft = usePane(sessionId, (s) => s.draft)
  const session = usePane(sessionId, (s) => s.session)
  const setDraft = useLogStore((s) => s.setDraft)
  const pushLocal = useLogStore((s) => s.pushLocal)
  const refreshTree = useAppStore((s) => s.refreshTree)

  // Один стрим на беседу: живёт от монтирования экрана до ухода назад.
  // Уход с экрана сессию на сервере не трогает — она продолжает работать.
  useEffect(() => {
    const stream = new SessionStream(sessionId)
    streamRef.current = stream
    stream.start()
    // Пока беседа открыта, через её сокет уходят и реплики, накопленные
    // офлайн-очередью: REST-ручки «написать в сессию» на сервере нет вовсе,
    // а без отправщика такие реплики копились бы молча и навсегда.
    const unregister = registerReplySender((id, text) => {
      const live = streamRef.current
      if (!live || id !== sessionId) return false
      live.send(text)
      return true
    })
    return () => {
      unregister()
      stream.dispose()
      streamRef.current = null
    }
  }, [sessionId])

  const state = session?.state ?? 'idle'
  const exited = state === 'exited'
  // Серверное значение главнее локального: после возврата на экран чип
  // сохранённой заметки берётся из session.notePath.
  const notePath = session?.notePath ?? savedPath

  const respond = useCallback((
    id: string, behavior: 'allow' | 'deny', opts?: PermissionOptions,
  ) => {
    streamRef.current?.respondPermission(id, behavior, opts)
  }, [])

  const submit = useCallback(() => {
    const text = draft.trim()
    if (!text || exited) return
    setDraft(sessionId, '')
    deliverInput(sessionId, text)
  }, [draft, exited, sessionId, setDraft])

  // Обычно сюда не попадаем: шторка грузит голосовое с autosend, и реплику
  // агенту отправляет сам сервер — даже если экран уже закрыт
  // (lib/voiceUpload.ts). Ниже — запасной путь для старого сервера.
  //
  // Расшифровка уходит в сессию репликой и оставляет в логе пометку — ровно
  // как на десктопе. В файл заметки она не пишется никогда (M9.3).
  //
  // Через очередь (queueAction), а не напрямую в streamRef: расшифровка —
  // это отложенный результат (сеть + whisper), и пока он едет, экран вполне
  // можно уже закрыть или уйти с него — тогда streamRef этого инстанса
  // обнулится, и прямой send() молча потерял бы реплику. Очередь переживает
  // уход с экрана (персистится) и доставит реплику, когда beседу откроют
  // заново — тем же путём, что уже работает для reply из шторки агента.
  const onVoiceText = useCallback((text: string, voiceId?: string | null) => {
    // строку «♪ голосовое · расшифровка сохранена» пишет сервер; своя — только для старого
    if (!voiceId) {
      pushLocal(sessionId, {
        kind: 'voice',
        gutter: null,
        text: 'голосовое → расшифровано, отправляю агенту',
      })
    }
    queueAction({ kind: 'reply', sessionId, text })
  }, [pushLocal, sessionId])

  const save = useCallback(async () => {
    if (saving) return
    setSaving(true)
    setNotice(null)
    try {
      // «↓» — это существующий api.saveNote: сервер собирает заметку-черновик
      // из выводов агента. В чужой открытый файл беседа не дописывается (M6.3).
      const note = await api.saveNote(sessionId, {})
      setSavedPath(note.path)
      void refreshTree()
      setNotice(`беседа в базе: ${note.path}`)
    } catch (error) {
      setNotice(describeError(error))
    } finally {
      setSaving(false)
    }
  }, [refreshTree, saving, sessionId])

  const stop = useCallback(() => {
    // строку про SIGINT пишет сервер; локально ничего не дублируем
    void api.interrupt(sessionId).catch(() => {
      streamRef.current?.interrupt()
    })
  }, [sessionId])

  const revive = useCallback(async () => {
    if (reviving) return
    setReviving(true)
    setNotice(null)
    try {
      await api.resume(sessionId)
      // новое состояние приедет status-сообщением стрима
    } catch (error) {
      setNotice(describeError(error))
    } finally {
      setReviving(false)
    }
  }, [reviving, sessionId])

  const hasText = draft.trim().length > 0

  return (
    <div className="ab-mchat">
      <ChatThread sessionId={sessionId} onRespond={respond} />

      {/* Прервать агента нужно ровно тогда, когда он работает: постоянная
          кнопка занимала бы место в строке ввода и ловилась бы пальцем
          вместо микрофона. */}
      {state === 'running' ? (
        <button type="button" className="ab-mchat__stop" onClick={stop}>
          <span className="ab-mchat__stopsq" aria-hidden /> остановить
        </button>
      ) : null}

      {notice ? (
        <button
          type="button"
          className="ab-mchat__notice"
          title="скрыть"
          onClick={() => setNotice(null)}
        >
          {notice}
        </button>
      ) : null}

      {exited ? (
        <div className="ab-mchat__dead">
          <span className="ab-mchat__deadtext">сессия завершена</span>
          <button
            type="button"
            className="ab-mchat__revive"
            disabled={reviving}
            onClick={() => { void revive() }}
          >
            {reviving ? 'оживляю…' : 'продолжить разговор'}
          </button>
        </div>
      ) : null}

      <VoicePending sessionId={sessionId} />
      <div className="ab-mcomposer">
        <button
          type="button"
          className={`ab-mcomposer__save${notePath ? ' is-saved' : ''}`}
          title={notePath
            ? `беседа сохранена: ${notePath}`
            : 'сохранить беседу в базу — заметка-черновик из выводов агента'}
          aria-label="сохранить беседу в базу"
          disabled={saving}
          onClick={() => { void save() }}
        >
          ↓
        </button>

        <input
          className="ab-mcomposer__field"
          value={draft}
          disabled={exited}
          placeholder={exited ? 'сессия завершена' : 'сообщение агенту'}
          onChange={(e) => setDraft(sessionId, e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
              e.preventDefault()
              submit()
            }
          }}
        />

        {/* Микрофон уступает место отправке, как только в поле появился текст:
            третью кнопку строка ввода на 390 px не вынесет, а отправлять
            только по «↵» на скрытой клавиатуре — тупик. */}
        {hasText ? (
          <button
            type="button"
            className="ab-mcomposer__send"
            aria-label="отправить"
            disabled={exited}
            onClick={submit}
          >
            ↑
          </button>
        ) : (
          <button
            type="button"
            className="ab-mcomposer__mic"
            aria-label="надиктовать"
            title="надиктовать"
            disabled={exited}
            onClick={() => setDictating(true)}
          >
            <span className="ab-mcomposer__micsq" aria-hidden />
          </button>
        )}
      </div>

      <DictationSheet
        open={dictating}
        target="chat"
        sessionId={sessionId}
        onClose={() => setDictating(false)}
        onText={onVoiceText}
        autosend
      />
    </div>
  )
}
